import { BrazeImageCard } from '@/components/braze/BrazeImageCard';
import BackButton from '@/components/buttons/BackButton';
import { ErrorAlert, InfoAlert, LoadingIconContainer } from '@/components/common/RebrandStyledComponents';
import type { IPrescriptions } from '@/components/patient/prescription/common';
import Prescription from '@/components/patient/prescription/Prescription';
import { FF_BRAZE_CONTENT_CARDS } from '@/constants/featureFlags';
import useGetPatientPrescriptions from '@/hooks/rest/useGetPatientPrescriptions';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { transformPrescriptions } from '@/utils';
import type { TypedAnalyticsContexts } from '@montugroup/data-collection';
import { AnalyticsTrackingProvider, buildContextId } from '@montugroup/data-collection';
import {
  Box,
  CircularProgress,
  Container,
  List,
  ListItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@montugroup/design-system';
import { TabContext, TabPanel } from '@mui/lab';
import { useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';

export default function PatientPrescriptions() {
  const theme = useTheme();
  const { flags } = useFeatureFlags();
  const [value, setValue] = useState(0);

  const { data: prescriptionsData, isLoading, isError, isSuccess } = useGetPatientPrescriptions();
  const { activePrescriptions, inactivePrescriptions } = transformPrescriptions(prescriptionsData);

  const renderContent = (prescriptions: IPrescriptions[], infoText: string) => {
    if (isError) {
      return (
        <ErrorAlert variant="standard" severity="error">
          An unexpected error occurred. Please try again.
        </ErrorAlert>
      );
    }

    if (isSuccess && prescriptions.length) {
      return (
        <Container maxWidth="lg" disableGutters sx={{ ml: 0 }}>
          <Stack component={List} gap={theme.spacing(5)} sx={{ padding: 0 }}>
            {prescriptions.map((prescription) => (
              <ListItem key={prescription.prescriptionCode} disableGutters disablePadding>
                <Prescription
                  prescriptionCode={prescription.prescriptionCode}
                  prescriptionMetadata={prescription.prescriptionMetadata}
                  prescription={prescription.prescriptions}
                />
              </ListItem>
            ))}
          </Stack>
        </Container>
      );
    }

    return <InfoAlert severity="info">{infoText}</InfoAlert>;
  };

  const prescriptionTabs = [
    {
      label: 'Active',
      children: renderContent(activePrescriptions, 'No active prescriptions'),
    },
    {
      label: 'Inactive',
      children: renderContent(inactivePrescriptions, 'No inactive prescriptions'),
    },
  ];

  const updateTrackingContext = useCallback(
    (ctx: TypedAnalyticsContexts) => {
      const contextId = buildContextId('prescriptions', value === 0 ? 'active' : 'inactive');
      return { ...ctx, uiEvent: { ...ctx.uiEvent, context_id: contextId } };
    },
    [value],
  );

  return (
    <AnalyticsTrackingProvider update={updateTrackingContext}>
      <Box display="flex">
        <BackButton />
        <Typography
          variant="h5"
          component="h1"
          sx={(theme) => ({ color: theme.palette.primary.main, lineHeight: theme.spacing(10) })}
        >
          Prescriptions
        </Typography>
      </Box>
      {flags[FF_BRAZE_CONTENT_CARDS] && (
        <Box sx={{ paddingTop: theme.spacing(6) }}>
          <BrazeImageCard />
        </Box>
      )}
      <Tabs
        value={value}
        onChange={(_event: React.SyntheticEvent, newValue: number) => setValue(newValue)}
        aria-label="Patient prescription tabs"
        sx={{
          marginTop: (theme) => theme.spacing(5),
        }}
      >
        {prescriptionTabs.map((tab, index) => (
          <Tab
            key={tab.label}
            id={`tab-${tab.label}`}
            label={tab.label}
            aria-controls={`tabpanel-${index}`}
            sx={(theme) => ({
              [theme.breakpoints.down('md')]: {
                width: '50%',
              },
            })}
          />
        ))}
      </Tabs>

      {isLoading ? (
        <LoadingIconContainer>
          <CircularProgress />
        </LoadingIconContainer>
      ) : (
        <TabContext value={value.toString()}>
          {prescriptionTabs.map((tab, index) => (
            <TabPanel key={tab.label} id={`tabpanel-${index}`} value={index.toString()}>
              {tab.children}
            </TabPanel>
          ))}
        </TabContext>
      )}
    </AnalyticsTrackingProvider>
  );
}
